import { useRef, useState } from "react";
import { Outlet, useLocation, useParams } from "react-router";
import { toast } from "sonner";
import { AlertMessage } from "@/components/ui/alert-message";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { SearchInput } from "@/components/ui/search-input";
import * as pcApi from "@/lib/api/portfolio-collections";
import { useDebouncedValue } from "@/lib/hooks/use-debounced-value";
import { useDeleteCheck } from "@/lib/hooks/use-delete-check";
import { useDeleteProduct } from "@/lib/hooks/use-portfolio-collections";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { ImportCsvDialog } from "./import-csv-dialog";
import { T } from "./page.i18n";
import { ProductDialog } from "./product-dialog";
import { ProductsInnerTabs } from "./products-inner-tabs";
import type { ProductsTabContext, ProductsTabDeleteTarget } from "./products-tab-context";

type DialogTab = "general" | "suitability" | "costs" | "allocation" | "documents";

const SUBPATH_TO_DIALOG_TAB: Record<string, DialogTab> = {
  general: "general",
  suitability: "suitability",
  cost: "costs",
  "asset-classes": "allocation",
  documents: "documents",
};

function deriveDialogTab(pathname: string): DialogTab {
  const match = pathname.match(/\/products\/([^/?#]+)/);
  return (match && SUBPATH_TO_DIALOG_TAB[match[1]!]) || "general";
}

export default function ProductsPage() {
  const { id } = useParams<{ id: string }>();
  const collectionId = id!;
  const { pathname } = useLocation();
  const localize = useLocalize();
  const t = useT(T);

  const deleteProduct = useDeleteProduct();

  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebouncedValue(searchInput, 300);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [csvDialogOpen, setCsvDialogOpen] = useState(false);
  const [dialogDefaultTab, setDialogDefaultTab] = useState<DialogTab>("general");
  const [editingProduct, setEditingProduct] = useState<any | null>(null);
  const [costTypes, setCostTypes] = useState<any[]>([]);
  const [assetClasses, setAssetClasses] = useState<any[]>([]);
  const [loadingProductId, setLoadingProductId] = useState<string | null>(null);
  const [loadingCreate, setLoadingCreate] = useState(false);

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState<ProductsTabDeleteTarget | null>(null);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertConfig, setAlertConfig] = useState({
    title: "",
    description: "",
    variant: "default" as "default" | "destructive",
  });

  const showAlert = (
    title: string,
    description: string,
    variant: "default" | "destructive" = "default",
  ) => {
    setAlertConfig({ title, description, variant });
    setAlertOpen(true);
  };

  const loadDialogData = async () => {
    const [ct, ac] = await Promise.all([
      pcApi.listCostTypes(collectionId),
      pcApi.listAssetClasses(collectionId),
    ]);
    setCostTypes(ct);
    setAssetClasses(ac);
  };

  const handleCreate = async () => {
    setLoadingCreate(true);
    try {
      await loadDialogData();
      setEditingProduct(null);
      setDialogDefaultTab("general");
      setDialogOpen(true);
    } finally {
      setLoadingCreate(false);
    }
  };

  const handleEdit = async (productId: string) => {
    setLoadingProductId(productId);
    try {
      const [product] = await Promise.all([
        pcApi.getProduct(collectionId, productId),
        loadDialogData(),
      ]);
      setEditingProduct(product);
      setDialogDefaultTab(deriveDialogTab(pathname));
      setDialogOpen(true);
    } catch {
      showAlert(t.error, t.failedToLoad, "destructive");
    } finally {
      setLoadingProductId(null);
    }
  };

  // QL-168 — a product the server would refuse to delete shows why on the
  // Delete click, before the confirmation. The sub-tabs pass the whole target;
  // the check answers by id, so the latest clicked target is kept here.
  const deleteTargetRef = useRef<ProductsTabDeleteTarget | null>(null);
  const { requestDelete, checkingId } = useDeleteCheck({
    check: (productId) => pcApi.checkDeleteProduct(collectionId, productId),
    onAllowed: (productId) => {
      const target = deleteTargetRef.current;
      if (target?.id !== productId) return;
      setProductToDelete(target);
      setConfirmDeleteOpen(true);
    },
    onBlocked: (reason) => showAlert(t.error, reason || t.failedToDelete, "destructive"),
  });

  const handleDeleteClick = (product: ProductsTabDeleteTarget) => {
    deleteTargetRef.current = product;
    void requestDelete(product.id);
  };

  const handleDeleteConfirm = () => {
    if (!productToDelete) return;
    deleteProduct.mutate(
      { collectionId, productId: productToDelete.id },
      {
        onSuccess: () => toast.success(t.productDeleted),
        onError: (err) => showAlert(t.error, err.message || t.failedToDelete, "destructive"),
      },
    );
    setProductToDelete(null);
  };

  const ctx: ProductsTabContext = {
    collectionId,
    debouncedSearch,
    openEdit: handleEdit,
    openDelete: handleDeleteClick,
    loadingProductId,
    checkingDeleteId: checkingId,
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">{t.description}</p>

      <ProductsInnerTabs />

      <div className="flex items-center justify-between gap-3">
        <SearchInput
          className="w-64"
          placeholder={t.searchPlaceholder}
          value={searchInput}
          onValueChange={setSearchInput}
        />
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setCsvDialogOpen(true)}>
            {t.loadCsv}
          </Button>
          <Button onClick={handleCreate} loading={loadingCreate}>
            {t.addProduct}
          </Button>
        </div>
      </div>
      <Outlet context={ctx} />

      <ProductDialog
        open={dialogOpen}
        portfolioCollectionId={collectionId}
        product={editingProduct}
        costTypes={costTypes}
        assetClasses={assetClasses}
        defaultTab={dialogDefaultTab}
        onClose={() => {
          setDialogOpen(false);
          setEditingProduct(null);
        }}
      />

      <ImportCsvDialog
        open={csvDialogOpen}
        portfolioCollectionId={collectionId}
        onClose={() => setCsvDialogOpen(false)}
      />

      <ConfirmDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        onConfirm={handleDeleteConfirm}
        title={t.deleteProduct}
        description={t.deleteConfirmation(
          productToDelete ? localize(productToDelete.nameI18n) : "",
        )}
      />

      <AlertMessage
        open={alertOpen}
        onOpenChange={setAlertOpen}
        title={alertConfig.title}
        description={alertConfig.description}
        variant={alertConfig.variant}
      />
    </div>
  );
}
